import { useEffect, type ComponentType } from 'react';
import {
  Clock,
  Crown,
  Flame,
  Lightning,
  Medal,
  Star,
  Sword,
  Trophy,
  type IconProps,
} from '@phosphor-icons/react';
import { Button, Card, Caption } from '@/ui';
import type { BadgeListItem } from './types';

/**
 * Modal de célébration affichée à Léon quand au moins un badge est débloqué
 * à l'issue d'une partie Practice Arena (Salve P2 H1).
 *
 * Reçoit la liste des badges déjà résolus (slug + name_fr + description_fr +
 * icon_glyph) via la prop `badges`. La résolution slug→BadgeListItem est faite
 * en amont (cf. PracticeReviewPage qui fetch `practiceApi.listBadges()` puis
 * filtre sur les slugs reçus via navigation state).
 *
 * UX :
 *   - Overlay fullscreen `ink-900/80 backdrop-blur-sm` (DESIGN.md modal).
 *   - Une `Card` par badge, animation `card-enter` cascadée (delay 70ms).
 *   - Bouton "Continuer" pour fermer (a11y : focus auto au mount, Esc ferme).
 *   - Si `badges` vide → composant rend `null` (responsabilité du parent de ne
 *     pas instancier mais on garde le garde-fou).
 *
 * Le mapping `GLYPH_TO_ICON` est dupliqué (cf. BadgesPage) — volontairement
 * pas extrait dans un module commun pour éviter la dépendance circulaire et
 * garder l'isolation visuelle des modules (CONVENTIONS.md §YAGNI).
 */

type PhosphorIcon = ComponentType<IconProps>;

const GLYPH_TO_ICON: Record<string, PhosphorIcon> = {
  Trophy,
  Sword,
  SwordCross: Sword,
  Lightning,
  Crown,
  Medal,
  Flame,
  Clock,
  Star,
};

function resolveBadgeIcon(glyph: string): PhosphorIcon {
  return GLYPH_TO_ICON[glyph] ?? Trophy;
}

interface Props {
  badges: BadgeListItem[];
  onClose: () => void;
}

export function BadgeUnlockedModal({ badges, onClose }: Props): JSX.Element | null {
  // Escape pour fermer (a11y minimal — DESIGN.md modal). Le focus est pris en
  // charge par le bouton "Continuer" via attribut `autoFocus` ci-dessous.
  useEffect(() => {
    if (badges.length === 0) return undefined;
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [badges.length, onClose]);

  if (badges.length === 0) return null;

  const title = badges.length === 1 ? 'Nouveau badge débloqué !' : `${badges.length} badges débloqués !`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/80 backdrop-blur-sm transition-opacity"
      role="dialog"
      aria-modal="true"
      aria-labelledby="badge-modal-title"
      data-testid="badge-unlocked-modal"
    >
      <div className="max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <Card accent="orange" padding="default" className="border-orange-500/60">
          <div className="flex items-center gap-3 mb-4">
            <Trophy size={28} weight="fill" className="text-orange-500" aria-hidden />
            <div>
              <Caption color="orange">Practice Arena</Caption>
              <h2
                id="badge-modal-title"
                className="text-heading-2 font-display font-semibold text-orange-500"
              >
                {title}
              </h2>
            </div>
          </div>

          <ul className="flex flex-col gap-3 mb-5" data-testid="badge-list">
            {badges.map((badge, idx) => {
              const Icon = resolveBadgeIcon(badge.icon_glyph);
              return (
                <li key={badge.slug} data-testid={`unlocked-badge-${badge.slug}`}>
                  <Card
                    padding="compact"
                    delay={idx * 70}
                    className="border-orange-500/40 bg-orange-500/[0.05]"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="flex items-center justify-center w-12 h-12 rounded-lg bg-orange-500/20 text-orange-500 shrink-0"
                        aria-hidden
                        data-glyph={badge.icon_glyph}
                      >
                        <Icon size={28} weight="fill" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-body-lg font-medium text-orange-500">
                          {badge.name_fr}
                        </h3>
                        <p className="text-body-sm text-ink-300 mt-1">{badge.description_fr}</p>
                      </div>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>

          <Button
            variant="primary"
            accent="orange"
            onClick={onClose}
            className="w-full"
          >
            Continuer
          </Button>
        </Card>
      </div>
    </div>
  );
}
